import { renderToString } from 'react-dom/server'
import { GetServerSideProps } from 'next'
import dynamic from 'next/dynamic'
import { InstantSearchServerState } from 'react-instantsearch'
import { getServerState } from 'react-instantsearch'
import { Document } from '@contentful/rich-text-types'
import { useContentfulLiveUpdates } from '@contentful/live-preview/react'

import { NotFoundPgPage } from 'pages/[[...slug]]'
import { homePagesRedirects } from 'homeRedirects'
import { floorPagesRedirects } from 'floorRedirects'
import { Layout } from '@components/layouts'
import { environment } from '@config/environment'
import { getHeaderAndFooter, getPageBySlug, getSynsisalWeaveColorGroupCollection } from '@services/contentful'
import { Default404Page } from '@utils/default-404'
import { getDisjunctiveFacets, getMatchedFacets as getMatchedFilterFacets } from '@utils/algolia'
import {
  ContentfulAtButton,
  ContentfulAtLink,
  ContentfulMlBeautyBanner,
  ContentfulMlCard,
  ContentfulMlContentStrip,
  ContentfulMlHeading,
  ContentfulMlInfo,
  ContentfulMlVertical,
  ContentfulMlVideo,
  ContentfulOrCollapseContainer,
  ContentfulOrContainer,
  ContentfulPgPage,
  ContentfulSynsisalWeaveColorGroup,
  SiteDomain,
  TemplateContentType,
} from 'types'

import { useHandleBackButtonPagination } from '@hooks/use-handle-back-button-pagination'
import { Hit } from 'instantsearch.js'
import { organizationDataForCatalog } from '@utils/scripts'
import { SearchResultsHit } from '@components/searchbox/Results'

const isSynsisalSite = environment.siteDomain === SiteDomain.SYNSISAL

const TmSynsisal = dynamic(() => import('@templates/tm-catalog/tm-synsisal').then((mod) => mod.default))
const TmAlgolia = dynamic(() => import('@templates/tm-catalog/tm-algolia').then((mod) => mod.default))
const TmVirtualAlgolia = dynamic(() => import('@templates/tm-catalog/tm-algolia').then((mod) => mod.TmVirtualAlgolia))
const TmGeneric = dynamic(() => import('@templates/tm-generic').then((mod) => mod.TmGeneric))
const TmCatalog = dynamic(() => import('@templates/tm-catalog').then((mod) => mod.TmCatalog))

const getPotentialRedirect = (slug: string) => {
  let newUrl
  if (environment.siteDomain === SiteDomain.FURNITURE) {
    newUrl = homePagesRedirects['/' + slug]
  } else {
    newUrl = floorPagesRedirects['/' + slug]
  }

  if (newUrl) {
    return {
      redirect: {
        destination: newUrl,
        permanent: true,
      },
    }
  }
}

export type TmCatalogProps = {
  ctaCard?: ContentfulMlCard & { description: Document }
  loadMoreButton?: ContentfulAtButton
  productBanner?: ContentfulMlContentStrip
  beautyBanner?: ContentfulMlBeautyBanner
  heading?: ContentfulMlHeading
  collapseContainer?: ContentfulOrCollapseContainer
  noResultsLabel?: string
  noResultsContent?: Document
  noResultsButtons?: ContentfulAtButton[]
  noResultsHelperText?: Document
  blocks?: (ContentfulOrContainer | ContentfulMlVertical | ContentfulMlInfo | ContentfulMlVideo)[]
  extraBlocks?: (ContentfulAtButton | ContentfulAtLink | ContentfulOrContainer)[]
} & {
  CONTENT_TYPE: TemplateContentType
}

export type ServerState = {
  serverState?: InstantSearchServerState
  url?: string
  serverFilterFacets?: string
  synsisalWeaveColorGroupCollection?: ContentfulSynsisalWeaveColorGroup[]
  breadcrumb?: string[]
}

export const getServerSideProps: GetServerSideProps<
  (ContentfulPgPage<TmCatalogProps> & ServerState) | NotFoundPgPage
> = async ({ req, resolvedUrl, preview }) => {
  const path = resolvedUrl.replace(/\?.*/, '').replace('/catalog', 'catalog')

  const potentialRedirect = getPotentialRedirect(path)
  if (potentialRedirect) {
    return potentialRedirect
  }

  const isPreview = preview ?? false
  const page = await getPageBySlug<TmCatalogProps>(path, isPreview)

  if (!page) {
    return { notFound: true }
  }

  const { footerContentful, headerContentful } = await getHeaderAndFooter(page, isPreview)

  if (page.template?.CONTENT_TYPE === 'tmCatalog') {
    const protocol = req.headers.referer?.split('://')[0] || 'https'
    const url = `${protocol}://${req.headers.host}${resolvedUrl}`

    const virtualServerState = await getServerState(<TmVirtualAlgolia />, {
      renderToString,
    })

    const virtualServerResults = virtualServerState?.initialResults[environment.algolia.recordsIndex].results[0]

    // Remove the "/catalog/" segment
    const paths = resolvedUrl.substring(9)
    // Validate the path and remove if query-params exists
    const pathArray = paths.indexOf('?') !== -1 ? paths.substring(0, paths.indexOf('?')).split('/') : paths.split('/')

    const allFacets = getDisjunctiveFacets(virtualServerResults?.facets)

    const virtualServerFilterFacets = !isSynsisalSite
      ? getMatchedFilterFacets({
          allFacets,
          pathArray,
        })
      : 'materialFamilySlug:synsisal'

    const serverState = await getServerState(<TmAlgolia url={url} serverFilterFacets={virtualServerFilterFacets} />, {
      renderToString,
    })

    const synsisalAlgoliaHitsIDs: string = isSynsisalSite
      ? serverState?.initialResults[environment.algolia.recordsIndex].results[0].hits
          .map((item: Hit) => item.objectID)
          .join(',')
      : ''

    const synsisalWeaveColorGroupCollection = await getSynsisalWeaveColorGroupCollection(
      synsisalAlgoliaHitsIDs,
      isSynsisalSite,
      isPreview,
    )

    const slugs = path.split('/')
    slugs.unshift('')
    let full_path = ''
    const baseUrl =
      environment.siteDomain === 'sisalcarpet' ? 'https://www.sisalcarpet.com/' : 'https://www.curranonline.com/'
    const breadcrumbPromise = slugs.map((path: string, index) => {
      if (index == 0) {
        return {
          '@type': 'ListItem',
          position: index,
          name: 'Home',
          item: baseUrl,
        }
      }
      if (index > 1) {
        full_path += '/'
      }
      let breadcrumb_name = path.replaceAll('-', ' ')
      if (breadcrumb_name && breadcrumb_name.length > 1) {
        breadcrumb_name = breadcrumb_name[0].toUpperCase() + breadcrumb_name.slice(1)
      }

      full_path += path
      return {
        '@type': 'ListItem',
        position: index,
        name: breadcrumb_name,
        item: baseUrl + full_path,
      }
    })

    return {
      props: {
        ...page,
        footer: footerContentful,
        header: headerContentful,
        isPreview,
        serverState,
        url,
        notFound: false,
        serverFilterFacets: virtualServerFilterFacets,
        synsisalWeaveColorGroupCollection,
        breadcrumb: breadcrumbPromise,
      },
    }
  }

  return {
    props: {
      ...page,
      footer: footerContentful,
      header: headerContentful,
      isPreview,
      notFound: false,
    },
  }
}

export default function PgCatalogPage(props: (ContentfulPgPage<TmCatalogProps> & ServerState) | NotFoundPgPage) {
  const update = useContentfulLiveUpdates(props)

  if ((update as NotFoundPgPage).notFound) {
    return <Default404Page />
  }

  const { seo, isPreview, header, announcement, footer, slug, template, blocks } =
    update as ContentfulPgPage<TmCatalogProps>
  const { url, serverState, serverFilterFacets, synsisalWeaveColorGroupCollection, breadcrumb } = update as ServerState
  const hits = serverState?.initialResults[environment.algolia.recordsIndex].results[0].hits as SearchResultsHit[]
  const structData = organizationDataForCatalog(
    hits,
    update as ContentfulPgPage<TmCatalogProps>,
    serverFilterFacets,
    breadcrumb ?? [],
  )
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const handleBackButtonPagination = useHandleBackButtonPagination()

  return (
    <Layout
      slug={slug}
      isPreview={isPreview}
      seo={seo}
      header={{ ...header, announcement: announcement || header.announcement }}
      footer={footer}
    >
      {(environment.siteDomain === 'sisalcarpet' || environment.siteDomain === 'furniture') &&
        environment.isProd &&
        !url?.includes('query=') && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: structData }} />
        )}
      {template?.CONTENT_TYPE === 'tmCatalog' ? (
        <TmCatalog
          key={slug}
          beautyBanner={template?.beautyBanner}
          productBanner={template?.productBanner}
          collapseContainer={template?.collapseContainer}
          heading={template?.heading}
          blocks={template?.blocks || []}
          extraBlocks={template?.extraBlocks}
        >
          {handleBackButtonPagination}
          {environment.siteDomain === SiteDomain.SYNSISAL ? (
            <TmSynsisal
              serverState={serverState}
              ctaCard={template?.ctaCard}
              serverFilterFacets={serverFilterFacets}
              loadMoreButton={template?.loadMoreButton}
              noResultsLabel={template?.noResultsLabel}
              noResultsContent={template?.noResultsContent}
              noResultsButtons={template?.noResultsButtons}
              noResultsHelperText={template?.noResultsHelperText}
              synsisalWeaveColorGroupCollection={synsisalWeaveColorGroupCollection}
            />
          ) : (
            <TmAlgolia
              url={url}
              serverState={serverState}
              serverFilterFacets={serverFilterFacets}
              ctaCard={template?.ctaCard}
              loadMoreButton={template?.loadMoreButton}
              noResultsLabel={template?.noResultsLabel}
              noResultsContent={template?.noResultsContent}
              noResultsButtons={template?.noResultsButtons}
              noResultsHelperText={template?.noResultsHelperText}
            />
          )}
        </TmCatalog>
      ) : (
        <TmGeneric blocks={blocks} />
      )}
    </Layout>
  )
}
